// Set up global MoEngage error suppression immediately
if (typeof window !== 'undefined' && !(window as any).__moengageErrorSuppressionActive) {
  setupMoengageErrorSuppression();
}

export const loadMoengageScript = async () => {
  // Comprehensive duplicate loading prevention
  if (document.getElementById('moengageScript') || 
      (window as any).Moengage || 
      (window as any).__moengageLoaded ||
      document.querySelector('script[src*="moe_webSdk"]')) {
    console.log('✅ MoEngage already loaded, skipping duplicate initialization');
    return;
  }
  
  // Mark as loading to prevent race conditions
  (window as any).__moengageLoaded = true;
  
  const MOENGAGE_DEBUG_LOGS = process.env.NEXT_PUBLIC_ENV === 'production' ? 0 : 1;
  const MOENGAGE_APP_ID = 'MDD9EYG5F5HXXDW8BF6IP4XW';
  
  // Disable service worker completely to avoid registration errors
  const supportsServiceWorker = false;
  
  // Pre-populate SDK settings to avoid "no data found" error
  const moengageSettings = {
    app_id: MOENGAGE_APP_ID,
    debug_logs: MOENGAGE_DEBUG_LOGS,
    cluster: 'DC_3',
    swPath: supportsServiceWorker ? '/sw.js' : undefined,
    initialized: true,
    timestamp: Date.now()
  };
  
  // Store settings in localStorage to prevent SDK errors
  try {
    localStorage.setItem('moe_web_sdk_settings', JSON.stringify(moengageSettings));
    localStorage.setItem('moe_web_sdk_config', JSON.stringify(moengageSettings));
  } catch (e) {
    console.warn('Failed to set MoEngage localStorage settings:', e);
  }

  const script = document.createElement('script');
  
  // Create the initialization configuration (no service worker to avoid errors)
  const initConfig = {
    app_id: MOENGAGE_APP_ID,
    debug_logs: MOENGAGE_DEBUG_LOGS,
    cluster: 'DC_3'
  };
  
  script.textContent = `
          (function (i, s, o, g, r, a, m, n) {
            i.moengage_object = r;
            t = {};
            q = function (f) {
              return function () {
                (i.moengage_q = i.moengage_q || []).push({ f: f, a: arguments });
              };
            };
            (f = [
              'track_event',
              'add_user_attribute',
              'add_first_name',
              'add_last_name',
              'add_email',
              'add_mobile',
              'add_user_name',
              'add_gender',
              'add_birthday',
              'destroy_session',
              'add_unique_user_id',
              'moe_events',
              'call_web_push',
              'track',
              'location_type_attribute',
            ]),
              (h = { onsite: ['getData', 'registerCallback'] });
            for (k in f) {
              t[f[k]] = q(f[k]);
            }
            for (k in h)
              for (l in h[k]) {
                null == t[k] && (t[k] = {}), (t[k][h[k][l]] = q(k + '.' + h[k][l]));
              }
            a = s.createElement(o);
            m = s.getElementsByTagName(o)[0];
            a.async = 1;
            a.src = g;
            m.parentNode.insertBefore(a, m);
            i.moe =
              i.moe ||
              function () {
                n = arguments[0];
                return t;
              };
            a.onload = function () {
              if (n) {
                i[r] = moe(n);
              }
            };
          })(
            window,
            document,
            'script',
            'https://cdn.moengage.com/webpush/moe_webSdk.min.latest.js',
            'Moengage'
          );
          
          // Initialize MoEngage after SDK loads - NO SERVICE WORKER to prevent errors
          setTimeout(function() {
            try {
              // Force initialization without any service worker
              window.Moengage = moe({
                app_id: '${MOENGAGE_APP_ID}',
                debug_logs: ${MOENGAGE_DEBUG_LOGS},
                cluster: 'DC_3'
                // Explicitly NO swPath to prevent service worker registration
              });
              console.log('✅ MoEngage initialized successfully (no service worker)');
            } catch (e) {
              console.log('⚠️ MoEngage initialization error (handled):', e.message);
            }
          }, 500);
    `;
  script.id = 'moengageScript';
  
  script.onload = () => {
    console.log('MOENGAGE SCRIPT LOADED');
    
    // Additional initialization check and setup
    setTimeout(() => {
      const moengage = (window as any).Moengage;
      if (moengage && (window as any).moe) {
        console.log('✅ MoEngage is ready for event tracking');
        
        // Add debug helper to window for testing
        (window as any).testMoEngage = () => {
          console.log('🧪 Testing MoEngage...');
          try {
            if (typeof moengage.track_event === 'function') {
              moengage.track_event('test_event', { 
                test_param: 'test_value',
                timestamp: new Date().toISOString() 
              });
              console.log('✅ Test event sent to MoEngage');
            } else {
              console.error('❌ MoEngage track_event function not available');
            }
          } catch (e) {
            console.error('❌ MoEngage test failed:', e);
          }
        };
        
        console.log('💡 Run testMoEngage() in console to test MoEngage integration');
      } else {
        console.warn('⚠️ MoEngage may not be properly initialized');
      }
    }, 1000);
  };

  script.onerror = (error) => {
    console.warn('MoEngage script load error (handled):', error);
  };

  document.body.appendChild(script);
}

export async function upsertMoengageClickIds(clickIds: { gclid?: string; fbclid?: string }) {
  if (typeof window === 'undefined') return;

  const gclid = (clickIds.gclid || '').trim();
  const fbclid = (clickIds.fbclid || '').trim();

  // Only act when user actually came from a campaign.
  if (!gclid && !fbclid) return;

  // Ensure MoEngage is present (loads once due to guards inside loadMoengageScript).
  if (!(window as any)?.Moengage) {
    try {
      await loadMoengageScript();
      // Give the SDK a moment to initialize.
      await new Promise(resolve => setTimeout(resolve, 800));
    } catch {
      // Best-effort only; don't block the app.
    }
  }

  const moengage = (window as any)?.Moengage;
  if (!moengage || typeof moengage.add_user_attribute !== 'function') return;

  try {
    // MoEngage overwrites user attributes with the latest value.
    if (gclid) moengage.add_user_attribute('gclid', gclid);
    if (fbclid) moengage.add_user_attribute('fbclid', fbclid);
  } catch {
    // Best-effort; errors are already globally suppressed in this module.
  }
}

// Global MoEngage error suppression function - Applied immediately on module load
function setupMoengageErrorSuppression() {
  if (typeof window === 'undefined') return;
  
  // Check if already applied
  if ((window as any).__moengageErrorSuppressionActive) {
    return;
  }
  
  (window as any).__moengageErrorSuppressionActive = true;
  
  // Store original console methods
  const originalConsoleError = console.error;
  const originalConsoleWarn = console.warn;
  const originalConsoleLog = console.log;
  
  // Comprehensive MoEngage error filter
  const moengageErrorFilter = (originalMethod: any, ...args: any[]) => {
    const errorStr = args.join(' ').toLowerCase();
    
    // Check if it's a MoEngage-related message
    const isMoengageMessage = errorStr.includes('moengage') || errorStr.includes('[[[ moengage ]');
    
    // Comprehensive list of MoEngage error patterns to suppress
    const isMoengageSuppressibleError = 
      isMoengageMessage &&
      (errorStr.includes('service worker register failed') ||
       errorStr.includes('error registering serviceworker') ||
       errorStr.includes('https-notificationmanager.registerserviceworker') ||
       errorStr.includes('https-notificationmanager.constructor') ||
       errorStr.includes('notificationmanager.registerserviceworker') ||
       errorStr.includes('notificationmanager.constructor') ||
       errorStr.includes('registerserviceworker') ||
       errorStr.includes('serviceworker registration failed') ||
       errorStr.includes('sw registration failed') ||
       errorStr.includes('push notification') ||
       errorStr.includes('notification permission') ||
       // User attribute related warnings (benign)
       errorStr.includes('attribute already present') ||
       errorStr.includes('attribute already queued') ||
       errorStr.includes('not adding again'));
    
    // Suppress MoEngage errors/warnings completely
    if (isMoengageSuppressibleError) {
      // Silently handle - no console output for cleaner logs
      return;
    }
    
    // Allow all other console outputs
    originalMethod.apply(console, args);
  };
  
  // Override console methods
  console.error = (...args: any[]) => moengageErrorFilter(originalConsoleError, ...args);
  console.warn = (...args: any[]) => moengageErrorFilter(originalConsoleWarn, ...args);
  
  // Handle global errors
  const globalErrorHandler = (event: ErrorEvent) => {
    const errorMsg = event.message?.toLowerCase() || '';
    if (errorMsg.includes('moengage') && 
        (errorMsg.includes('service worker') || errorMsg.includes('notification'))) {
      event.preventDefault();
      event.stopPropagation();
      return false;
    }
  };
  
  // Handle promise rejections
  const rejectionHandler = (event: PromiseRejectionEvent) => {
    const reason = event.reason?.toString()?.toLowerCase() || '';
    if (reason.includes('moengage') || 
        (reason.includes('service worker') && reason.includes('register')) || 
        reason.includes('notification')) {
      event.preventDefault();
      return;
    }
  };
  
  window.addEventListener('error', globalErrorHandler);
  window.addEventListener('unhandledrejection', rejectionHandler);
  
  // Restore after extended time to ensure all MoEngage initialization attempts are covered
  setTimeout(() => {
    console.error = originalConsoleError;
    console.warn = originalConsoleWarn;
    window.removeEventListener('error', globalErrorHandler);
    window.removeEventListener('unhandledrejection', rejectionHandler);
    (window as any).__moengageErrorSuppressionActive = false;
    console.log('🔇 MoEngage error suppression period ended');
  }, 30000); // 30 seconds to cover all possible registration attempts
};
